import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ProbablePitcher {
  id: string;
  game_pk: number;
  game_date: string;
  player_id: string | null;
  mlb_player_id: number;
  player_name: string;
  team_abbreviation: string | null;
  opponent_abbreviation: string | null;
  home_away: string;
  start_time: string | null;
  is_confirmed: boolean;
}

export interface PitcherStartInfo {
  player_id: string;
  starts: ProbablePitcher[];
  isTwoStart: boolean;
  nextStart: ProbablePitcher | null;
}

/**
 * Fetches probable pitchers for the next 7 days and computes
 * two-start flags and next-start info per player.
 */
export function useProbablePitchers(weekStart?: string, weekEnd?: string) {
  const [probables, setProbables] = useState<ProbablePitcher[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const end = new Date();
      end.setDate(end.getDate() + 7);
      const endStr = end.toISOString().split('T')[0];

      const fromDate = weekStart || today;
      const toDate = weekEnd || endStr;

      const { data, error } = await supabase
        .from('probable_pitchers')
        .select('*')
        .gte('game_date', fromDate)
        .lte('game_date', toDate)
        .order('game_date', { ascending: true });

      if (!error && data) {
        setProbables(data as ProbablePitcher[]);
      }
      setLoading(false);
    };
    fetch();
  }, [weekStart, weekEnd]);

  // Index by player_id for quick lookup
  const byPlayerId = useMemo(() => {
    const map = new Map<string, PitcherStartInfo>();
    const today = new Date().toISOString().split('T')[0];

    for (const p of probables) {
      if (!p.player_id) continue;
      if (!map.has(p.player_id)) {
        map.set(p.player_id, {
          player_id: p.player_id,
          starts: [],
          isTwoStart: false,
          nextStart: null,
        });
      }
      const info = map.get(p.player_id)!;
      info.starts.push(p);
      if (p.game_date >= today && !info.nextStart) {
        info.nextStart = p;
      }
    }

    // Flag two-start pitchers
    for (const info of map.values()) {
      info.isTwoStart = info.starts.length >= 2;
    }

    return map;
  }, [probables]);

  // Convenient list of two-start pitcher IDs
  const twoStartPitcherIds = useMemo(() => {
    const ids: string[] = [];
    for (const [id, info] of byPlayerId) {
      if (info.isTwoStart) ids.push(id);
    }
    return new Set(ids);
  }, [byPlayerId]);

  return {
    probables,
    byPlayerId,
    twoStartPitcherIds,
    loading,
    getPlayerInfo: (playerId: string) => byPlayerId.get(playerId) || null,
  };
}
